import React, { useMemo } from 'react'
import { History } from 'history'

import {
  GetProjectsQuery,
  GetSharesQuery,
  useGetProjectsQuery,
  useGetSharesQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { useDebounceValue } from '@sketch/utils'

import * as S from '../components/CommandBar.styles'
import { normalizeCommandBarSearchText } from '../utils'
import { CommandBarItem } from '../types'
import { thumbnailForPreviewFile } from '../../shares/utils'
import { routes } from '../../routes'

import { ReactComponent as DocumentsIcon } from '@sketch/icons/documents-2-17'
import { ReactComponent as FolderIcon } from '@sketch/icons/folder-closed-17'

interface UseCommandBarWorkspaceResultsProps {
  history: History
  isOpen: boolean
  query: string
  workspace?: WorkspaceMinimalFragment
}

type Share = GetSharesQuery['workspace']['shares']['entries'][number]
type Project = GetProjectsQuery['workspace']['projects']['entries'][number]

const MIN_REMOTE_QUERY_LENGTH = 2
const MAX_REMOTE_RESULTS_PER_TYPE = 5

const createDocumentResult = (
  share: Share,
  history: History
): CommandBarItem => {
  const thumbnailUrl = thumbnailForPreviewFile(
    share.version?.document?.previewFile
  )

  return {
    id: `document.${share.identifier}`,
    analyticsId: 'document',
    kind: 'document',
    title: share.name,
    subtitle: share.project?.name || 'Document',
    section: 'Documents',
    iconNode: (
      <S.PreviewIconFrame>
        {thumbnailUrl ? (
          <S.PreviewIconImage src={thumbnailUrl} alt="" aria-hidden="true" />
        ) : (
          <DocumentsIcon />
        )}
      </S.PreviewIconFrame>
    ),
    run: () =>
      history.push(routes.SHARE_VIEW.create({ shareID: share.identifier })),
  }
}

const createProjectResult = (
  project: Project,
  workspaceId: string,
  history: History
): CommandBarItem => ({
  id: `folder.${project.identifier}`,
  analyticsId: 'folder',
  kind: 'folder',
  title: project.name,
  subtitle: project.topLevelProject?.name || 'Folder',
  section: 'Folders',
  icon: FolderIcon,
  run: () =>
    history.push(
      routes.WORKSPACE_PROJECT.create({
        workspaceId,
        projectId: project.identifier,
      })
    ),
})

export const useCommandBarWorkspaceResults = ({
  history,
  isOpen,
  query,
  workspace,
}: UseCommandBarWorkspaceResultsProps) => {
  const debouncedQuery = useDebounceValue(query, 250)
  const normalizedQuery = normalizeCommandBarSearchText(debouncedQuery)
  const shouldSearchRemote =
    isOpen && !!workspace && normalizedQuery.length >= MIN_REMOTE_QUERY_LENGTH

  const sharesResult = useGetSharesQuery({
    variables: {
      identifier: workspace?.identifier || '',
      after: null,
      search: {
        name: normalizedQuery,
        filters: [],
      },
    },
    skip: !shouldSearchRemote,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const projectsResult = useGetProjectsQuery({
    variables: {
      workspaceId: workspace?.identifier || '',
      after: null,
    },
    skip: !shouldSearchRemote,
    fetchPolicy: 'cache-first',
  })

  const documentResults = useMemo(
    () =>
      (sharesResult.data?.workspace.shares.entries || [])
        .slice(0, MAX_REMOTE_RESULTS_PER_TYPE)
        .map(share => createDocumentResult(share, history)),
    [history, sharesResult.data?.workspace.shares.entries]
  )

  const projectResults = useMemo(() => {
    if (!workspace) {
      return []
    }

    const allProjects = [
      ...(projectsResult.data?.workspace.projects.entries || []),
      ...(projectsResult.data?.workspace.draftsProject.entries || []),
    ]

    return allProjects
      .filter(project =>
        normalizeCommandBarSearchText(project.name).includes(normalizedQuery)
      )
      .slice(0, MAX_REMOTE_RESULTS_PER_TYPE)
      .map(project =>
        createProjectResult(project, workspace.identifier, history)
      )
  }, [
    history,
    normalizedQuery,
    projectsResult.data?.workspace.draftsProject.entries,
    projectsResult.data?.workspace.projects.entries,
    workspace,
  ])

  return {
    loading: sharesResult.loading || projectsResult.loading,
    error: sharesResult.error || projectsResult.error,
    results: shouldSearchRemote ? [...documentResults, ...projectResults] : [],
  }
}
